<template>
  <lf-dropdown
    ref="dropdown"
    class="w-full"
    :value="value || undefined"
    :placeholder="placeholder"
    :options="dropdownOptions"
    :is-loading="isInitialLoading"
    :name="name"
    :full-width="fullWidth"
    :show-clear-filter="showClearFilter"
    :as-badge="asBadge"
    :teleport-options="teleportOptions"
    :search-placeholder="searchTerm"
    :warning-as-error="warningAsError"
    :drop-up="dropUp"
    :disabled="disabled"
    :sort="sort"
    search-enabled
    :menu-width="menuWidth"
    :show-warning-icon="showWarningIcon"
    @change="handleOptionChange"
    @toggle="handleDropdownToggled"
    @search:change="handleSearchChange"
  >
    <template v-if="$slots['toggle-button']" #toggle-button="scope">
      <slot name="toggle-button" v-bind="scope" />
    </template>
    <template #label="{ option, isSelected, optionId }">
      <slot
        name="option"
        :option="option"
        :options="paginatedData"
        :is-selected="isSelected"
        :option-id="optionId"
      />
    </template>
    <template #scroll-sentinel>
      <scroll-sentinel
        :can-load-more="hasMore"
        :is-loading="isLoadingNextPage"
        :root="optionsList"
        @load-more="loadMore"
      />
    </template>
  </lf-dropdown>
</template>

<script lang="ts">
import { computed, ref, toRef } from "vue";

import get from "lodash/get";

import type { IPaginatedResponse, PaginatedOptionsMeta } from "@/models/common";

export const NONE_OPTION_ID = "0";

type LfDropdownPropTypes = InstanceType<typeof LfDropdown>["$props"];

interface Props {
  teleportOptions?: LfDropdownPropTypes["teleportOptions"];
  asBadge?: LfDropdownPropTypes["asBadge"];
  fullWidth?: LfDropdownPropTypes["fullWidth"];
  showClearFilter?: LfDropdownPropTypes["showClearFilter"];
  warningAsError?: LfDropdownPropTypes["warningAsError"];
  value?: string | number | boolean;
  placeholder?: string;
  accessor: string;
  name?: string;
  searchTerm?: string;
  includeNone?: boolean;
  noneOptionLabel?: string;
  identifier?: string;
  showWarningIcon?: boolean;
  dropUp?: boolean;
  disabled?: boolean;
  sort?: boolean;
  menuWidth?: string;
  // Disabling no-any for time being
  // TODO: Use component generics once available in Vue 3.3.0
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getPaginatedOptions?: (...params: any[]) => any;
}
</script>

<script setup lang="ts">
import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";
import ScrollSentinel from "@/components/ui/inputs/ScrollSentinel.vue";

const emit = defineEmits<{
  // TODO use component generics once available
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (e: "change", val: any): void;
  (e: "loading", loading: boolean): void;
}>();

const props = withDefaults(defineProps<Props>(), {
  value: "",
  placeholder: "",
  name: "",
  searchTerm: "",
  fullWidth: false,
  showClearFilter: false,
  asBadge: false,
  includeNone: false,
  identifier: "id",
  warningAsError: false,
  showWarningIcon: false,
  dropUp: false,
  disabled: false,
  sort: false,
});

const dropdown = ref<InstanceType<typeof LfDropdown> | null>(null);
const search = ref("");
const isLoadingMore = ref(false);
const paginatedData = ref<unknown[]>([]);
const paginatedOptionsMeta = ref<PaginatedOptionsMeta>({
  current_page: 0,
  last_page: null,
});

const hasMore = computed(() => {
  if (!props.getPaginatedOptions) {
    return false;
  }
  return (
    paginatedOptionsMeta.value.current_page !==
    Number(paginatedOptionsMeta.value.last_page)
  );
});

const isInitialLoading = computed(
  () => isLoadingMore.value && paginatedData.value.length === 0
);

const isLoadingNextPage = computed(
  () => isLoadingMore.value && paginatedData.value.length > 0
);

const optionsList = toRef(() => dropdown.value?.optionsList ?? null);

const dropdownOptions = computed(() => {
  let options = paginatedData.value.reduce<Record<string, string>>(
    (acc, item) => {
      const typedItem = item as Record<string, unknown>;
      const id = typedItem[props.identifier] as string;
      const value = get(typedItem, props.accessor) as string;
      acc[id] = value;
      return acc;
    },
    {}
  );

  if (props.includeNone) {
    options = { [NONE_OPTION_ID]: props.noneOptionLabel ?? "None", ...options };
  }

  return options;
});

const resetOptions = () => {
  paginatedData.value = [];
  paginatedOptionsMeta.value = {
    current_page: 0,
    last_page: null,
  };
};

const handleSearchChange = (value: string, load = true) => {
  if (value === search.value) {
    return;
  }
  search.value = value;
  if (dropdown.value) {
    dropdown.value.search = value;
  }
  resetOptions();
  if (load) {
    loadMore();
  }
};

const handleOptionChange = (val: string | number) => {
  if (props.includeNone && val.toString() === NONE_OPTION_ID) {
    emit("change", undefined);
    handleSearchChange("", false);
    return;
  }
  const selectedValue = paginatedData.value.find(
    (item) =>
      (item as Record<string, unknown>)[props.identifier]?.toString() ===
      val.toString()
  );
  emit("change", selectedValue);
  handleSearchChange("", false);
};

const handleDropdownToggled = (value: boolean) => {
  if (!paginatedOptionsMeta.value.current_page && value) {
    loadMore();
  }
};

const loadMore = async (params: Record<string, string | string[]> = {}) => {
  if (!props.getPaginatedOptions) {
    return;
  }
  const { current_page, last_page } = paginatedOptionsMeta.value;
  if (current_page === last_page) {
    return;
  }
  try {
    isLoadingMore.value = true;
    emit("loading", true);

    const pageToGet = current_page + 1;
    const payload: Record<string, string | string[] | number | number[]> = {
      ...params,
      page: pageToGet,
    };
    if (search.value) {
      payload.search = search.value;
    }
    const response: IPaginatedResponse<unknown> =
      await props.getPaginatedOptions(payload);
    paginatedData.value.push(...response.data);
    paginatedOptionsMeta.value.current_page = response.meta.current_page;
    paginatedOptionsMeta.value.last_page = response.meta.last_page;

    emit("loading", false);
    return response.data;
  } catch {
    return [];
  } finally {
    isLoadingMore.value = false;
  }
};

const resetPaginatedOptionsMeta = () => {
  paginatedOptionsMeta.value = {
    current_page: 0,
    last_page: null,
  };
};

defineExpose({
  resetOptions,
  loadMore,
  paginatedData,
  handleSearchChange,
  resetAll: () => {
    resetOptions();
    handleSearchChange("", false);
  },
  resetPaginatedOptionsMeta,
});
</script>
